import React, { useMemo, useState } from 'react';
import { builtInCommands, type PaletteCommand } from './built-in-commands';
import { fuzzyFilter } from './fuzzy-match';
import { isConversational } from './is-conversational';
import { CommandItem } from './CommandItem';

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (target: string) => void;
  /**
   * Omit when the company does not have AI. The palette stays useful for
   * navigation; it just stops offering an answer it cannot produce.
   */
  onAskAi?: (query: string) => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose, onNavigate, onAskAi }) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const matchedCommands = useMemo<PaletteCommand[]>(() => {
    if (!query.trim()) return builtInCommands;
    return fuzzyFilter(builtInCommands, query.trim(), (c) => c.label).map((r) => r.item);
  }, [query]);

  const showAiFallback =
    !!onAskAi && query.trim().length > 0 && (matchedCommands.length === 0 || isConversational(query));
  const totalItems = matchedCommands.length + (showAiFallback ? 1 : 0);

  const selectIndex = (idx: number) => {
    if (idx < matchedCommands.length) {
      const cmd = matchedCommands[idx]!;
      if (cmd.kind === 'navigate') onNavigate(cmd.target);
    } else if (showAiFallback && onAskAi) {
      onAskAi(query);
    }
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(totalItems - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectIndex(activeIndex);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        data-testid="palette-scrim"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 19 }}
      />
      <div
        role="dialog"
        aria-label="Command palette"
        data-testid="command-palette"
        style={{
          position: 'fixed', top: 60, left: '50%', transform: 'translateX(-50%)',
          width: 520, maxHeight: '60vh', overflow: 'auto',
          background: 'white', border: '2px solid #8b5cf6', borderRadius: 10,
          boxShadow: '0 20px 48px rgba(0,0,0,0.3)', zIndex: 20,
        }}
        onKeyDown={onKeyDown}
      >
        <input
          autoFocus
          type="text"
          value={query}
          aria-label="Palette query"
          onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
          placeholder="Type a command or ask LognAI…"
          style={{ width: '100%', padding: '12px 16px', fontSize: 14, border: 'none', borderBottom: '1px solid #e4e4e7', outline: 'none' }}
        />
        <div>
          {matchedCommands.map((cmd, i) => (
            <CommandItem key={cmd.id} label={cmd.label} kind="command" active={i === activeIndex} onClick={() => selectIndex(i)} />
          ))}
          {showAiFallback && (
            <CommandItem
              label={`Ask LognAI: "${query}"`}
              kind="ai"
              active={matchedCommands.length === activeIndex}
              onClick={() => selectIndex(matchedCommands.length)}
            />
          )}
          {matchedCommands.length === 0 && !showAiFallback && (
            <div style={{ padding: '12px 16px', color: '#71717a', fontSize: 13 }}>No results</div>
          )}
        </div>
      </div>
    </>
  );
};
